import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ChevronLeft, ExternalLink, Star, Save, Plus, Trash2, ChevronDown } from "lucide-react";
import { api, authHeader, getAdminToken, clearAdminToken } from "@/lib/api";
import MilestoneMedia from "@/components/MilestoneMedia";

const STATUSES = ["Not Started", "In Progress", "Complete"];

const BriefTab = ({ pid }) => {
  const [b, setB] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    (async () => {
      try { const r = await api.get(`/admin/projects/${pid}`, { headers: authHeader() }); setB(r.data); }
      catch { toast.error("Failed to load"); }
    })();
  }, [pid]);
  if (!b) return <div className="py-16 flex justify-center"><Loader2 className="animate-spin" size={20} /></div>;
  const u = (k, v) => setB({ ...b, [k]: v });
  const upI = (i, v) => { const a = [...b.included]; a[i] = v; setB({ ...b, included: a }); };
  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/admin/projects/${pid}`, {
        name: b.name, brand_name: b.brand_name || "",
        scope: b.scope, timeline: b.timeline, included: b.included, drive_url: b.drive_url,
        show_captions: b.show_captions ?? true,
      }, { headers: authHeader() });
      setB(r.data);
      toast.success("Brief saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  return (
    <div className="mt-10 max-w-3xl" data-testid="admin-brief-form">
      <div><label className="label-eyebrow">Brand name</label>
        <input data-testid="brief-brand-input" className="senk-input mt-2" value={b.brand_name || ""} onChange={(e) => u("brand_name", e.target.value)} placeholder="e.g. Atelier Noir" /></div>
      <div className="mt-10"><label className="label-eyebrow">Project name</label>
        <input data-testid="brief-name-input" className="senk-input mt-2" value={b.name} onChange={(e) => u("name", e.target.value)} /></div>
      <div className="mt-10"><label className="label-eyebrow">Scope</label>
        <textarea data-testid="brief-scope-input" rows={5} className="senk-input resize-none mt-2" value={b.scope} onChange={(e) => u("scope", e.target.value)} /></div>
      <div className="mt-10"><label className="label-eyebrow">Timeline</label>
        <input data-testid="brief-timeline-input" className="senk-input mt-2" value={b.timeline} onChange={(e) => u("timeline", e.target.value)} placeholder="Feb 12 — Apr 30, 2026" /></div>
      <div className="mt-10"><label className="label-eyebrow">Google Drive URL</label>
        <input data-testid="brief-drive-input" className="senk-input mt-2" value={b.drive_url} onChange={(e) => u("drive_url", e.target.value)} placeholder="https://drive.google.com/..." /></div>
      <div className="mt-10">
        <label className="label-eyebrow flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            data-testid="brief-show-captions"
            checked={b.show_captions ?? true}
            onChange={(e) => u("show_captions", e.target.checked)}
          />
          Show media captions on portal
        </label>
      </div>
      <div className="mt-10">
        <div className="flex items-center justify-between">
          <label className="label-eyebrow">What&apos;s included</label>
          <button type="button" onClick={() => setB({ ...b, included: [...b.included, ""] })} data-testid="brief-included-add" className="btn-ghost btn-type" style={{ padding: "0.5rem 1rem" }}>
            <Plus size={14} strokeWidth={2} /> Add
          </button>
        </div>
        <ul className="mt-4 space-y-3">
          {b.included.map((it, i) => (
            <li key={i} className="flex items-center gap-3">
              <span className="font-serif-display italic w-8 shrink-0" style={{ color: "var(--senk-body)" }}>{String(i + 1).padStart(2, "0")}</span>
              <input data-testid={`brief-included-${i}`} className="senk-input" value={it} onChange={(e) => upI(i, e.target.value)} />
              <button type="button" onClick={() => setB({ ...b, included: b.included.filter((_, x) => x !== i) })} className="p-2" style={{ color: "var(--senk-body)" }}><Trash2 size={16} strokeWidth={1.5} /></button>
            </li>
          ))}
        </ul>
      </div>
      <button onClick={save} disabled={saving} data-testid="brief-save" className="btn-primary btn-type mt-12">
        {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={14} strokeWidth={2} />}<span>Save Brief</span>
      </button>
    </div>
  );
};

const MilestonesTab = ({ pid }) => {
  const [items, setItems] = useState(null);
  const [saving, setSaving] = useState(false);
  const [openIdx, setOpenIdx] = useState(null);
  useEffect(() => {
    (async () => {
      try { const r = await api.get(`/admin/projects/${pid}/milestones`, { headers: authHeader() }); setItems(r.data); }
      catch { toast.error("Failed to load"); }
    })();
  }, [pid]);
  if (!items) return <div className="py-16 flex justify-center"><Loader2 className="animate-spin" size={20} /></div>;
  const u = (i, k, v) => setItems(items.map((r, x) => x === i ? { ...r, [k]: v } : r));
  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/admin/projects/${pid}/milestones`, { milestones: items }, { headers: authHeader() });
      setItems(r.data); toast.success("Milestones saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  return (
    <div className="mt-10" data-testid="admin-milestones-form">
      <ul className="space-y-4">
        {items.map((m, i) => (
          <li key={i} className="pb-4" style={{ borderBottom: "0.5px solid var(--senk-border)" }}>
            <div className="grid grid-cols-12 gap-3 items-center">
              <span className="col-span-1 font-serif-display italic" style={{ color: "var(--senk-body)" }}>{String(i + 1).padStart(2, "0")}</span>
              <input data-testid={`milestone-name-${i}`} className="senk-input col-span-5" placeholder="Name" value={m.name} onChange={(e) => u(i, "name", e.target.value)} />
              <input type="date" data-testid={`milestone-date-${i}`} className="senk-input col-span-2" value={m.due_date} onChange={(e) => u(i, "due_date", e.target.value)} />
              <select data-testid={`milestone-status-${i}`} className="senk-input col-span-2" value={m.status} onChange={(e) => u(i, "status", e.target.value)}>
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <button type="button" onClick={() => setOpenIdx(openIdx === i ? null : i)} data-testid={`milestone-toggle-${i}`} className="col-span-1 label-eyebrow flex items-center gap-1 justify-center">
                Media <ChevronDown size={12} strokeWidth={2} style={{ transform: openIdx === i ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />
              </button>
              <button type="button" onClick={() => { setItems(items.filter((_, x) => x !== i)); if (openIdx === i) setOpenIdx(null); }} className="col-span-1 p-2 justify-self-end" style={{ color: "var(--senk-body)" }}><Trash2 size={16} strokeWidth={1.5} /></button>
            </div>
            {openIdx === i && (
              <div className="mt-4 pl-12">
                <MilestoneMedia projectId={pid} milestoneIndex={i} editable={true} />
              </div>
            )}
          </li>
        ))}
      </ul>
      <div className="flex items-center gap-3 mt-8">
        <button type="button" onClick={() => setItems([...items, { name: "", status: "Not Started", due_date: "" }])} data-testid="milestone-add" className="btn-ghost btn-type">
          <Plus size={14} strokeWidth={2} /> Add milestone
        </button>
        <button onClick={save} disabled={saving} data-testid="milestones-save" className="btn-primary btn-type">
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={14} strokeWidth={2} />}<span>Save Milestones</span>
        </button>
      </div>
      <p className="label-eyebrow mt-6" style={{ color: "var(--senk-body)" }}>
        Note: media is attached to a milestone&apos;s position in the list. Save first, then upload, and avoid reordering while media is attached.
      </p>
    </div>
  );
};

const SubmissionsTab = ({ pid, kind }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    (async () => {
      try { const r = await api.get(`/admin/projects/${pid}/${kind}`, { headers: authHeader() }); setData(r.data); }
      catch { toast.error("Failed to load"); }
    })();
  }, [pid, kind]);
  if (!data) return <div className="py-16 flex justify-center"><Loader2 className="animate-spin" size={20} /></div>;
  const fmt = (iso) => new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
  if (data.length === 0) return <p className="mt-10" style={{ color: "var(--senk-body)" }}>No {kind} yet.</p>;
  return (
    <ul className="mt-10" data-testid={`admin-${kind}-list`} style={{ borderTop: "0.5px solid var(--senk-border)" }}>
      {data.map((it) => (
        <li key={it.id} className="py-8" style={{ borderBottom: "0.5px solid var(--senk-border)" }}>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <p className="label-eyebrow">{fmt(it.created_at)}</p>
            {kind === "feedback" && it.name && <p className="label-eyebrow" style={{ color: "var(--senk-fg)" }}>From {it.name}</p>}
            {kind === "testimonials" && (
              <div className="flex items-center gap-1">{[1,2,3,4,5].map((n) => (
                <Star key={n} size={16} strokeWidth={1.25} className={n <= it.rating ? "fill-[#2E2E2C] text-[#2E2E2C]" : "fill-none text-[#C8C4BC]"} />
              ))}</div>
            )}
          </div>
          <p className="mt-3 font-serif-display text-xl md:text-2xl leading-snug" style={{ color: "var(--senk-fg)" }}>“{it.message}”</p>
        </li>
      ))}
    </ul>
  );
};

export default function AdminProject() {
  const { id } = useParams();
  const nav = useNavigate();
  const [tab, setTab] = useState("brief");
  const [proj, setProj] = useState(null);
  const [authed] = useState(!!getAdminToken());

  const reload = useCallback(async () => {
    try { const r = await api.get(`/admin/projects/${id}`, { headers: authHeader() }); setProj(r.data); }
    catch (e) {
      if (e.response?.status === 401) { clearAdminToken(); nav("/admin"); }
      else if (e.response?.status === 404) { toast.error("Project not found"); nav("/admin"); }
    }
  }, [id, nav]);
  useEffect(() => { reload(); }, [reload]);

  if (!authed) return <Navigate to="/admin" replace />;
  if (!proj) return <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--senk-bg)" }}><Loader2 className="animate-spin" size={24} /></div>;

  const portalUrl = `${window.location.origin}/portal/${proj.slug}`;
  const tabs = [
    { id: "brief", label: "Brief" },
    { id: "milestones", label: "Milestones" },
    { id: "feedback", label: "Feedback" },
    { id: "testimonials", label: "Testimonials" },
  ];

  return (
    <main className="min-h-screen" style={{ background: "var(--senk-bg)", color: "var(--senk-fg)" }}>
      <header style={{ borderBottom: "0.5px solid var(--senk-border)" }}>
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-6 flex items-center justify-between gap-4">
          <button onClick={() => nav("/admin")} data-testid="back-to-projects" className="label-eyebrow flex items-center gap-2 hover:opacity-100" style={{ opacity: 0.7 }}>
            <ChevronLeft size={14} strokeWidth={2} /> All projects
          </button>
          <a href={portalUrl} target="_blank" rel="noopener noreferrer" className="btn-ghost btn-type" data-testid="open-portal-link">
            <ExternalLink size={14} strokeWidth={2} /> /portal/{proj.slug}
          </a>
        </div>
        <div className="max-w-6xl mx-auto px-6 md:px-12 pb-6">
          <p className="label-eyebrow">{proj.brand_name ? proj.brand_name : (proj.archived ? "Archived" : "Active project")}</p>
          <h1 className="font-serif-display text-4xl md:text-5xl mt-2" data-testid="admin-project-name">{proj.name || "Untitled"}</h1>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 md:px-12 py-12">
        <div className="flex items-center gap-8 pb-4 flex-wrap" style={{ borderBottom: "0.5px solid var(--senk-border)" }}>
          {tabs.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`} className="label-eyebrow pb-2"
              style={{ borderBottom: tab === t.id ? "1.5px solid var(--senk-fg)" : "1.5px solid transparent",
                       color: tab === t.id ? "var(--senk-fg)" : "var(--senk-body)" }}>{t.label}</button>
          ))}
        </div>
        {tab === "brief" && <BriefTab pid={id} />}
        {tab === "milestones" && <MilestonesTab pid={id} />}
        {tab === "feedback" && <SubmissionsTab pid={id} kind="feedback" />}
        {tab === "testimonials" && <SubmissionsTab pid={id} kind="testimonials" />}
      </div>
    </main>
  );
}
